import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AppState, useAppDispatch } from '../../../../../config/store';

import { useSelector } from 'react-redux';
import { useFirestoreConnect } from 'react-redux-firebase';
import ErrorBoundary from '../../../../../components/ErrorBoundary';
import { useModal } from '../../../../../components/Modal';
import { UNSPECIFIED_LABEL } from '../../../../../config/constants';
import { ALL_ROLES } from '../../../../../config/roles';
import useSurvey from '../../../../../hooks/useSurvey';
import SurveyorAssignmentSelector, {
  type RoleLike,
} from '../../components/SurveyorAssignmentSelector';
import { EMPTY_ARRAY } from '../../../../../shared/helpers';
import { canManageMultipleSurveyorAssignments } from '../../../../../shared/auth';
import {
  getMainSurveyorIdOrUnassigned,
  isUnassignedSurveyorId,
  UNASSIGNED_SURVEYOR_OPTION,
} from '../../../../../shared/surveyorAssignment';
import {
  getReissueAssignmentValidation,
  getSelectedSurveyorIdsForReissue,
  uniqueNonEmpty,
} from '../../../../../shared/reissueAssignment';
import { isReissueConfigReady } from './reissueConfigGate';
import { onSelectedSurveyReissue } from '../../../../../state/survey';
import { Statuses } from '../../../../../types/status';
import { UrlParams } from '../../../../../types/urlParams';
import { Role } from '../../../../../types/user';

type AssignmentState = {
  mainSurveyorId: string;
  additionalSurveyorIds: string[];
};

const Reissue = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { showModal } = useModal();
  const [resetRooms, setResetRooms] = useState<boolean>(false);
  const [assignment, setAssignment] = useState<AssignmentState>({
    mainSurveyorId: '',
    additionalSurveyorIds: [],
  });
  const [isAssignmentDirty, setIsAssignmentDirty] = useState(false);
  const survey = useSurvey();
  const { clientId } = useParams<UrlParams>();

  // @ts-ignore
  useFirestoreConnect([
    {
      collection: 'roles',
      where: [
        ['clientId', '==', clientId],
        ['role', 'in', [ALL_ROLES.SURVEYOR, ALL_ROLES.LEAD_SURVEYOR]],
        ['deleted', '==', 0],
      ],
      storeAs: 'surveyors',
    },
  ]);

  useFirestoreConnect([
    {
      collection: 'roles',
      where: [
        ['clientId', '==', clientId],
        ['role', 'not-in', [ALL_ROLES.SURVEYOR, ALL_ROLES.LEAD_SURVEYOR]],
        ['deleted', '==', 0],
      ],
      storeAs: 'otherUsers',
    },
  ]);

  const surveyors: Role[] = useSelector(
    (state: AppState) => state.firestore.ordered.surveyors || EMPTY_ARRAY,
  );
  const surveyDoc = useSelector(
    (state: AppState) => state.firestore.data.survey,
  );
  const surveyFormConfigDoc = useSelector(
    (state: AppState) => state.firestore.data.surveyFormConfig,
  );
  const surveyFloorPlansConfigDoc = useSelector(
    (state: AppState) => state.firestore.data.surveyFloorPlansConfig,
  );
  const otherUsers: Role[] = useSelector(
    (state: AppState) => state.firestore.ordered.otherUsers || EMPTY_ARRAY,
  );
  const userRoles = useSelector(
    (state: AppState) => state.firebase.profile.token?.claims?.roles,
  );
  const canManageMultipleSurveyors = canManageMultipleSurveyorAssignments({
    roles: userRoles,
    clientId,
  });
  const isConfigReadyForReissue = useMemo(
    () =>
      isReissueConfigReady({
        surveyDoc,
        formConfigDoc: surveyFormConfigDoc,
        floorPlansConfigDoc: surveyFloorPlansConfigDoc,
      }),
    [surveyDoc, surveyFormConfigDoc, surveyFloorPlansConfigDoc],
  );

  const surveyorsWithUnassigned: RoleLike[] = useMemo(() => {
    return [...surveyors, UNASSIGNED_SURVEYOR_OPTION];
  }, [surveyors]);

  const surveyorsById = useMemo(() => {
    return surveyorsWithUnassigned.reduce((acc, surveyor) => {
      acc[surveyor.uid] = surveyor;
      return acc;
    }, {} as Record<string, RoleLike>);
  }, [surveyorsWithUnassigned]);

  const currentSurveyorIds = useMemo(() => {
    const sourceIds =
      Array.isArray(survey?.surveyorIds) && survey.surveyorIds.length
        ? survey.surveyorIds
        : [survey?.surveyorId];
    return uniqueNonEmpty(sourceIds);
  }, [survey?.surveyorId, survey?.surveyorIds]);

  const sourceAssignment = useMemo(() => {
    const fallbackMainSurveyorId =
      survey?.surveyorId && survey.surveyorId !== UNSPECIFIED_LABEL
        ? survey.surveyorId
        : getMainSurveyorIdOrUnassigned(currentSurveyorIds[0]);
    return {
      mainSurveyorId: fallbackMainSurveyorId,
      additionalSurveyorIds: currentSurveyorIds.filter(
        (uid) => uid !== fallbackMainSurveyorId,
      ),
    };
  }, [currentSurveyorIds, survey?.surveyorId]);

  const sourceAssignmentSignature = useMemo(() => {
    return `${
      sourceAssignment.mainSurveyorId
    }|${sourceAssignment.additionalSurveyorIds.join(',')}`;
  }, [sourceAssignment]);

  useEffect(() => {
    setIsAssignmentDirty(false);
  }, [survey?.id]);

  useEffect(() => {
    if (!survey?.id || isAssignmentDirty) {
      return;
    }
    setAssignment(sourceAssignment);
  }, [
    isAssignmentDirty,
    sourceAssignment,
    sourceAssignmentSignature,
    survey?.id,
  ]);

  const selectedSurveyorIds = useMemo(() => {
    return getSelectedSurveyorIdsForReissue({
      mainSurveyorId: assignment.mainSurveyorId,
      additionalSurveyorIds: assignment.additionalSurveyorIds,
      canManageMultipleSurveyors,
    });
  }, [
    assignment.additionalSurveyorIds,
    assignment.mainSurveyorId,
    canManageMultipleSurveyors,
  ]);

  const unavailableAssignedIds = useMemo(() => {
    return currentSurveyorIds.filter((uid) => !surveyorsById[uid]);
  }, [currentSurveyorIds, surveyorsById]);

  const getSurveyorDisplayName = (uid: string) => {
    const selectedSurveyor = surveyorsById[uid];
    if (selectedSurveyor) {
      return `${selectedSurveyor.nameFirst} ${selectedSurveyor.nameLast}`;
    }
    const otherUser = otherUsers.find((user) => user.uid === uid);
    if (otherUser) {
      return `${otherUser.nameFirst} ${otherUser.nameLast}`;
    }
    return survey?.surveyors?.[uid]?.name || 'User Not Found';
  };
  const currentAssignedSurveyorNames = useMemo(() => {
    return currentSurveyorIds.map(getSurveyorDisplayName);
  }, [currentSurveyorIds, surveyorsById, survey?.surveyors]);

  const assignmentDiff = useMemo(() => {
    const currentAssignedSurveyorIds = currentSurveyorIds.filter(
      (uid) => !isUnassignedSurveyorId(uid),
    );
    const selectedAssignedSurveyorIds = selectedSurveyorIds.filter(
      (uid) => !isUnassignedSurveyorId(uid),
    );
    const currentSet = new Set(currentAssignedSurveyorIds);
    const nextSet = new Set(selectedAssignedSurveyorIds);
    const addedIds = selectedAssignedSurveyorIds.filter(
      (uid) => !currentSet.has(uid),
    );
    const removedIds = currentAssignedSurveyorIds.filter(
      (uid) => !nextSet.has(uid),
    );

    return {
      addedIds,
      removedIds,
      changed: addedIds.length > 0 || removedIds.length > 0,
    };
  }, [currentSurveyorIds, selectedSurveyorIds]);

  const showConfirmationWarning =
    survey.status === Statuses.IN_PROGRESS && assignmentDiff.changed;

  const { mainSurveyorUnavailable, canConfirm } =
    getReissueAssignmentValidation({
      assignment,
      surveyorsById,
      canManageMultipleSurveyors,
    });

  const onConfirm = async () => {
    if (!canConfirm) {
      return;
    }

    if (showConfirmationWarning) {
      const added = assignmentDiff.addedIds.map(getSurveyorDisplayName);
      const removed = assignmentDiff.removedIds.map(getSurveyorDisplayName);
      const removedMessages = removed.length
        ? [
            'Removed surveyors will no longer have access to this survey.',
            '',
            'Please ensure that any removed surveyors have connected to the internet from their surveying device and checked that their survey data is fully uploaded before proceeding.',
            '',
            'They can do this by navigating to the survey within the Asseticom app and checking whether it shows as "Synced To Cloud"',
            '',
            'Proceeding to reissue without checking may result in data loss.',
          ]
        : [];

      const proceed = await showModal({
        title: 'Confirm Reissue Assignment Change',
        messages: [
          'This survey is currently in progress and you are changing surveyor assignments.',
          '',
          ...(added.length ? [`Added: ${added.join(', ')}`] : ['Added: None']),
          ...(removed.length
            ? [`Removed: ${removed.join(', ')}`]
            : ['Removed: None']),
          '',
          ...removedMessages,
          '',
          'Do you want to continue?',
        ].filter((m) => m !== undefined && m !== null),
        buttons: {
          confirm: [{ text: 'Continue', value: true }],
          cancel: [{ text: 'Cancel', value: false }],
        },
      });

      if (!proceed) {
        return;
      }
    }

    const mainSurveyor = surveyorsById[assignment.mainSurveyorId];
    const selectedSurveyorsById = selectedSurveyorIds.reduce(
      (acc, uid) => {
        const selectedSurveyor = surveyorsById[uid];
        if (selectedSurveyor) {
          acc[uid] = {
            uid: selectedSurveyor.uid,
            nameFirst: selectedSurveyor.nameFirst,
            nameLast: selectedSurveyor.nameLast,
            email: selectedSurveyor.email,
          };
        }
        return acc;
      },
      {} as Record<
        string,
        {
          uid: string;
          nameFirst: string;
          nameLast: string;
          email: string;
        }
      >,
    );

    dispatch(
      onSelectedSurveyReissue({
        resetRooms,
        survey,
        assignment: {
          mainSurveyorId: assignment.mainSurveyorId,
          mainSurveyorName: mainSurveyor
            ? `${mainSurveyor.nameFirst} ${mainSurveyor.nameLast}`
            : getSurveyorDisplayName(assignment.mainSurveyorId),
          mainSurveyorEmail:
            mainSurveyor?.email || survey?.surveyorEmail || UNSPECIFIED_LABEL,
          additionalSurveyorIds: selectedSurveyorIds.filter(
            (uid) => uid !== assignment.mainSurveyorId,
          ),
          selectedSurveyorsById,
        },
      }),
    );
    history.push(`/${clientId}/surveys`);
  };

  if (!isConfigReadyForReissue) {
    return null;
  }

  return (
    <Paper sx={{ width: '100%', p: 2 }} id="main-content">
      <ErrorBoundary>
        <Typography
          variant="body1"
          fontSize={18}
          mb={2}
          color="secondary"
          paragraph
        >
          Reissue
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {survey.status === Statuses.IN_PROGRESS ? (
          <>
            <Box
              sx={{
                border: 1,
                borderColor: 'error.main',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography color="error" paragraph my={2} fontWeight={700}>
                This survey is currently in progress
              </Typography>
              <Typography color="error" paragraph my={2}>
                Currently assigned surveyor:{' '}
                <b>
                  {canManageMultipleSurveyors
                    ? currentAssignedSurveyorNames.join(', ') || 'None'
                    : survey.surveyorName || 'None'}
                </b>
              </Typography>
              {survey.uploadsQueueSize ? (
                <Typography color="error" paragraph my={2}>
                  There are currently <b>{survey.uploadsQueueSize} or more</b>{' '}
                  images that have not yet been uploaded.
                </Typography>
              ) : null}
              <Typography color="error" paragraph my={2}>
                Before removing any surveyors, please ensure that they have
                connected to the internet from their surveying device and
                checked that their survey data is fully uploaded.
                <br />
                <br />
                They can do this by navigating to the survey within the
                Asseticom app and checking whether it shows as {''}
                <b>&apos;Synced To Cloud&apos;</b>.
                <br />
                <br />
                <b>
                  Proceeding to reissue without checking may result in data
                  loss.
                </b>
                <br />
                <br />
                Should you be unsure of the status of your survey data please{' '}
                <Link color="secondary" href="mailto:support@asseticom.co.uk">
                  contact us
                </Link>{' '}
              </Typography>
            </Box>
            <Divider sx={{ my: 4 }} />
          </>
        ) : null}

        <Box my={2}>
          <Typography color="secondary" paragraph>
            Select {canManageMultipleSurveyors ? 'surveyors' : 'a surveyor'}{' '}
            below in order to issue the{' '}
            <Typography component="span" color="secondary" fontWeight={700}>
              {survey.name}
            </Typography>{' '}
            survey.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <SurveyorAssignmentSelector
            surveyors={surveyorsWithUnassigned}
            mainSurveyorId={assignment.mainSurveyorId}
            additionalSurveyorIds={assignment.additionalSurveyorIds}
            onChange={(nextAssignment) => {
              setIsAssignmentDirty(true);
              setAssignment(nextAssignment);
            }}
            maxAdditional={5}
            mainRequired
            errors={{ main: mainSurveyorUnavailable }}
            hideAdditional={!canManageMultipleSurveyors}
          />
        </Grid>

        {canManageMultipleSurveyors && unavailableAssignedIds.length ? (
          <Box my={2}>
            <Typography color="warning.main">
              Some previously assigned surveyors are no longer available and
              must be replaced:{' '}
              {unavailableAssignedIds.map(getSurveyorDisplayName).join(', ')}.
            </Typography>
          </Box>
        ) : null}

        {assignmentDiff.changed ? (
          <Box my={2}>
            <Typography color="secondary">
              Assignment changes:
              <br />
              Added:{' '}
              {assignmentDiff.addedIds.map(getSurveyorDisplayName).join(', ') ||
                'None'}
              <br />
              Removed:{' '}
              {assignmentDiff.removedIds
                .map(getSurveyorDisplayName)
                .join(', ') || 'None'}
            </Typography>
            {assignmentDiff.removedIds.length ? (
              <Typography color="error" mt={1} paragraph>
                Removed surveyors will no longer have access to this survey.
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box my={2}>
          <FormGroup>
            <FormControlLabel
              componentsProps={{ typography: { color: 'secondary' } }}
              control={
                <Checkbox
                  checked={resetRooms}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setResetRooms(event.target.checked);
                  }}
                />
              }
              label="Reset room statuses to 'Not Started'?"
            />
            <Typography variant="caption" color="secondary">
              Switching on this setting will mean all room statuses are reset
              and per-surveyor room status tracking is cleared.
            </Typography>
          </FormGroup>
        </Box>

        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={!canConfirm}
        >
          Confirm
        </Button>
      </ErrorBoundary>
    </Paper>
  );
};

export default Reissue;
